import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { useClaimCountRealtime } from '@/core/hooks/use-claim-requests';
import { useNavigateShell } from '@/core/hooks/use-navigate-shell';
import {
  notificationUnreadCountQueryKey,
  useNotificationMutations,
} from '@/core/hooks/use-notifications';
import { useWorkspaceRealtime } from '@/core/hooks/use-workspace-realtime';
import { showNotificationToast } from '@/core/lib/show-notification-toast';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { useAuthStore } from '@/core/store/auth.store';
import { useCurrentWorkspace } from '@/core/store/workspace.store';
import type { RealtimeEvent } from '@/core/types/realtime-events';

/**
 * The shell's one workspace realtime stream, open on every tab: notification
 * toasts and inbox counts, plus the claim nav badge (DEV-802).
 */
export function ShellRealtimeSync() {
  const workspace = useCurrentWorkspace();
  const userId = useAuthStore((state) => state.user?.id);
  const queryClient = useQueryClient();
  const { navigateShellDeepLink } = useNavigateShell();
  const dict = useDictionary();
  const { markRead } = useNotificationMutations();
  const viewLabel = dict.workspace.notifications.view;
  const onClaimEvent = useClaimCountRealtime();

  const onEvent = useCallback(
    (event: RealtimeEvent) => {
      onClaimEvent(event);
      if (event.type !== 'notification.created') return;
      if (!workspace?.id || event.workspaceId !== workspace.id) return;
      if (userId && event.userId !== userId) return;

      void queryClient.invalidateQueries({
        queryKey: ['notifications', 'inbox', workspace.id],
      });
      void queryClient.invalidateQueries({
        queryKey: notificationUnreadCountQueryKey(workspace.id),
      });

      showNotificationToast({
        notificationId: event.notificationId,
        title: event.title,
        body: event.body,
        deepLink: event.deepLink,
        viewLabel,
        onMarkRead: markRead,
        onNavigate: (path) => {
          navigateShellDeepLink(path);
        },
      });
    },
    [
      markRead,
      navigateShellDeepLink,
      onClaimEvent,
      queryClient,
      userId,
      viewLabel,
      workspace,
    ],
  );

  useWorkspaceRealtime({
    workspaceId: workspace?.id,
    enabled: Boolean(workspace?.id && userId),
    onEvent,
  });

  return null;
}
